import {skipToken} from '@reduxjs/toolkit/query';
import {differenceInCalendarDays} from 'date-fns';
import {
  Alert,
  type Action,
  Attributes,
  type AttributesRow,
  type ButtonProps,
  Card,
  DataStatus,
  Flag,
  openDialog,
} from 'platform/components';
import {Box, Hide, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {
  convertStringLocaleToDateFns,
  DateTimeFormat,
  useDateTimeFormatter,
  useLocale,
} from 'platform/locale';
import {match} from 'ts-pattern';

import type {ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  type ConfigurationDetailResponseBody,
  type ConfigurationDiffItemResponseBody,
  getManualStatusTargets,
  type PriceSnapshotLineResponseBody,
  type VehicleOrderDetailResponseBody,
  type VehicleOrderStatus,
  useGetVehicleOrderQuery,
} from '@dms/api/new-vehicle';
import {
  ConfiguratorStepper,
  getNewVehicleDiffPathLabel,
  getNewVehicleEnumColorScheme,
  getNewVehicleEnumLabel,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {buildArray, composePath, EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {DeliveryUpdatesCard} from './DeliveryUpdatesCard';
import {
  CancelOrderDialog,
  ChangeFactoryReferenceDialog,
  HandoverOrderDialog,
  RecordArrivalDialog,
  UpdateStatusDialog,
} from './LifecycleDialogs';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
const LIFECYCLE_STATUSES: readonly VehicleOrderStatus[] = [
  'ordered',
  'in_production',
  'in_transit',
  'arrived',
  'ready_for_handover',
  'handed_over',
];
/** Statuses where the car is still on its way, so a passed ETA means the importer is late. */
const EN_ROUTE_STATUSES: readonly VehicleOrderStatus[] = ['ordered', 'in_production', 'in_transit'];

function Row(props: {label: string; value?: ReactNode | string | null}) {
  const empty = props.value === undefined || props.value === null || props.value === '';
  return (
    <HStack spacing={4} align="flex-start">
      <Box width={44} flexShrink={0}>
        <Text size="small" color="secondary">
          {props.label}
        </Text>
      </Box>
      {typeof props.value === 'string' || empty ? (
        <Text size="small" alternative color={empty ? 'tertiary' : 'primary'}>
          {empty ? EMPTY_PLACEHOLDER : props.value}
        </Text>
      ) : (
        props.value
      )}
    </HStack>
  );
}

/** Two balanced columns on desktop, one on mobile — shared so every block lines up. */
function AttributeColumns(props: {rows: AttributesRow[]}) {
  const split = Math.ceil(props.rows.length / 2);
  return (
    <>
      <Hide onMobile>
        <HStack spacing={4} align="flex-start">
          <Attributes rows={props.rows.slice(0, split)} />
          <Attributes rows={props.rows.slice(split)} />
        </HStack>
      </Hide>
      <Show onMobile>
        <Attributes rows={props.rows} />
      </Show>
    </>
  );
}

function EtaContent(props: {order: VehicleOrderDetailResponseBody}) {
  const formatDate = useDateTimeFormatter();
  const {localeConfig, language} = useLocale();
  if (isNil(props.order.eta)) {
    return <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>;
  }
  const eta = parseDate(props.order.eta);
  const overdueDays = differenceInCalendarDays(new Date(), eta);
  const isOverdue = EN_ROUTE_STATUSES.includes(props.order.status) && overdueDays > 0;
  // Not formatDate('dateRelative'): it rounds the time since the ETA's midnight, so its day count
  // runs one ahead of overdueDays from noon on. platform/locale has no calendar-day relative format,
  // so the locale is resolved the way formatDateTime resolves it for the date next to this label.
  const {code} = localeConfig.dateFnsLocale ?? convertStringLocaleToDateFns(language);
  const overdueAge = new Intl.RelativeTimeFormat(code).format(-overdueDays, 'day');
  return (
    <HStack spacing={2} align="center">
      <Text size="xSmall">{formatDate('dateMedium', eta)}</Text>
      <Show when={isOverdue}>
        <Flag
          size="small"
          colorScheme="red"
          isSubtle
          label={`${i18n.t('general.labels.overdue')} · ${overdueAge}`}
          data-testid="new-vehicle-eta-overdue"
        />
      </Show>
    </HStack>
  );
}

const displayDiffValue = (
  value: ConfigurationDiffItemResponseBody['orderedValue'],
  lines: PriceSnapshotLineResponseBody[]
) => {
  const values = Array.isArray(value) ? value : [value];
  const resolved = values
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .map(
      (id) =>
        lines.find((line) => line.id === id)?.label ??
        t('entity.newVehicle.labels.unknownSelection', 'Unavailable selection')
    );
  return resolved.length > 0 ? resolved.join(', ') : EMPTY_PLACEHOLDER;
};

function ArrivalCard(props: {
  order: VehicleOrderDetailResponseBody;
  lines: PriceSnapshotLineResponseBody[];
}) {
  const arrival = props.order.arrival;
  if (!arrival) {
    return null;
  }
  return (
    <Card title={t('entity.newVehicle.order.arrivalSection', 'Arrival')}>
      <VStack spacing={3}>
        <Text alternative>
          <DateTimeFormat value={parseDate(arrival.arrivedOn)} format="dateMedium" /> ·{' '}
          {arrival.vin ?? EMPTY_PLACEHOLDER}
        </Text>
        <Show when={arrival.note}>
          <Text>{arrival.note}</Text>
        </Show>
        <Show when={arrival.diff.changed}>
          <Alert
            variant="warning"
            message={t(
              'entity.newVehicle.order.specificationDiffers',
              'Delivered specification differs from the order'
            )}
          />
          {arrival.diff.items.map((item) => (
            <VStack key={item.path} spacing={1}>
              <Text size="small" alternative>
                {getNewVehicleDiffPathLabel(item.path, t)}
              </Text>
              <Row
                label={t('entity.newVehicle.order.orderedValue', 'Ordered')}
                value={displayDiffValue(item.orderedValue, props.lines)}
              />
              <Row
                label={t('entity.newVehicle.order.observedValue', 'Observed')}
                value={displayDiffValue(item.observedValue, props.lines)}
              />
            </VStack>
          ))}
        </Show>
      </VStack>
    </Card>
  );
}

export function OrderDeliveryTab(props: {detail: ConfigurationDetailResponseBody}) {
  const [canReadOrder, canUpdateStatus, canCancel, canHandOver, canChangeReference] =
    usePermissions({
      permissionKeys: [
        'newVehicleOrderRead',
        'newVehicleOrderUpdateStatus',
        'newVehicleOrderCancel',
        'newVehicleOrderMarkHandedOver',
        'newVehicleOrderChangeFactoryReference',
      ],
    });
  const formatDate = useDateTimeFormatter();
  const detail = props.detail;
  const dateValue = (value: string | Nullish) =>
    isNil(value) ? null : formatDate('dateMedium', parseDate(value));
  const orderId = canReadOrder ? detail.order?.id : undefined;
  const orderQuery = useGetVehicleOrderQuery(isNotNil(orderId) ? {orderId} : skipToken);
  const shouldLoadOrder = isNotNil(orderId);
  const order = orderQuery.currentData;
  const currentIndex = order ? LIFECYCLE_STATUSES.indexOf(order.status) : 0;
  const manualStatusTargets = order ? getManualStatusTargets(order) : [];
  const lifecycleAvailability = order
    ? match(order.status)
        .with('cancelled', 'handed_over', () => ({
          arrival: false,
          handover: false,
          cancel: false,
        }))
        .with('arrived', 'ready_for_handover', () => ({
          arrival: false,
          handover: true,
          cancel: true,
        }))
        .otherwise(() => ({arrival: !order.arrival, handover: false, cancel: true}))
    : {arrival: false, handover: false, cancel: false};
  const availability = {
    ...lifecycleAvailability,
    update: manualStatusTargets.length > 0,
    changeReference: order?.status !== 'handed_over',
  };
  const openArrival = (order: VehicleOrderDetailResponseBody) =>
    openDialog(<RecordArrivalDialog order={order} vehicleVin={detail.vehicle?.vin} />, {
      title: t('entity.newVehicle.actions.recordArrival', 'Record arrival'),
    });
  const openCancel = (order: VehicleOrderDetailResponseBody) =>
    openDialog(<CancelOrderDialog order={order} />, {
      title: t('entity.newVehicle.actions.cancelOrder', 'Cancel order'),
    });
  const actions = order
    ? buildArray<Action>()
        .when(canUpdateStatus && availability.update, {
          type: 'button',
          title: t('entity.newVehicle.actions.updateStatus', 'Update status'),
          onClick: () =>
            openDialog(<UpdateStatusDialog order={order} />, {
              title: t('entity.newVehicle.actions.updateStatus', 'Update status'),
            }),
        })
        .when(canUpdateStatus && availability.arrival, {
          type: 'button',
          variant: 'secondary',
          title: t('entity.newVehicle.actions.recordArrival', 'Record arrival'),
          onClick: () => openArrival(order),
        })
        .when(canHandOver && availability.handover, {
          type: 'button',
          title: t('entity.newVehicle.actions.handOver', 'Hand over'),
          onClick: () =>
            openDialog(<HandoverOrderDialog order={order} />, {
              title: t('entity.newVehicle.actions.handOver', 'Hand over'),
            }),
        })
        .when(canChangeReference && availability.changeReference, {
          type: 'button',
          variant: 'secondary',
          title: t('entity.newVehicle.actions.editImporterReference', 'Edit reference'),
          onClick: () =>
            openDialog(<ChangeFactoryReferenceDialog order={order} />, {
              title: t('entity.newVehicle.actions.editImporterReference', 'Edit reference'),
            }),
        })
        .when(canCancel && availability.cancel, {
          type: 'button',
          variant: 'dangerOutlined',
          title: t('entity.newVehicle.actions.cancelOrder', 'Cancel order'),
          onClick: () => openCancel(order),
        })
    : [];
  const awaitingConfirmation =
    isNotNil(order) && isNotNil(order.awaitingConfirmation)
      ? match(order.awaitingConfirmation.kind)
          .with('delivered', () => ({
            title: t(
              'entity.newVehicle.order.factoryReportsDelivered',
              'Factory reports the vehicle delivered — confirm by recording the arrival'
            ),
            actions: buildArray<ButtonProps>().when(canUpdateStatus && availability.arrival, {
              title: t('entity.newVehicle.actions.recordArrival', 'Record arrival'),
              onClick: () => openArrival(order),
            }),
          }))
          .with('cancelled', () => ({
            title: t(
              'entity.newVehicle.order.factoryReportsCancelled',
              'Factory reports the order cancelled — confirm by cancelling the order'
            ),
            actions: buildArray<ButtonProps>().when(canCancel && availability.cancel, {
              variant: 'dangerOutlined',
              title: t('entity.newVehicle.actions.cancelOrder', 'Cancel order'),
              onClick: () => openCancel(order),
            }),
          }))
          .exhaustive()
      : null;
  const orderAttributes = order
    ? buildArray<AttributesRow>()
        .when(order.status === 'cancelled', {
          label: t('entity.newVehicle.order.cancellationReason', 'Cancellation reason'),
          value: order.cancellationReason,
        })
        .add({
          label: t('entity.newVehicle.order.source', 'Source'),
          value: getNewVehicleEnumLabel('orderSource', order.source, t),
        })
        .add({
          label: t('entity.newVehicle.order.importerReference', 'Importer order reference'),
          value: order.importerOrderRef,
        })
        .add({
          label: t('entity.newVehicle.order.orderedOn', 'Ordered on'),
          value: dateValue(order.orderedOn),
        })
        .add({
          label: t('entity.newVehicle.order.eta', 'Estimated arrival'),
          content: <EtaContent order={order} />,
        })
        .add({
          label: t('entity.newVehicle.order.arrivedOn', 'Arrived on'),
          value: dateValue(order.arrival?.arrivedOn),
        })
        .add({
          label: t('entity.newVehicle.order.specFreezeDate', 'Specification freeze date'),
          value: dateValue(order.specFreezeDate),
        })
    : [];
  const businessCaseId = detail.configuration.businessCaseId;

  return (
    <VStack spacing={4}>
      <Card
        title={detail.configuration.label}
        actions={actions}
        data-testid="new-vehicle-order"
        flags={
          order
            ? [
                {
                  label: getNewVehicleEnumLabel('orderStatus', order.status, t),
                  colorScheme: getNewVehicleEnumColorScheme('orderStatus', order.status),
                  isSubtle: true,
                },
              ]
            : undefined
        }
      >
        <VStack spacing={4}>
          <DataStatus
            data-testid="new-vehicle-order-status"
            isLoading={shouldLoadOrder && orderQuery.isLoading}
            isError={shouldLoadOrder && orderQuery.error !== undefined}
            errorMessage={t(
              'entity.newVehicle.order.loadError',
              'Delivery details could not be loaded'
            )}
            errorAction={{
              title: i18n.t('general.actions.retry'),
              onClick: orderQuery.refetch,
            }}
          >
            <Show when={order && order.status !== 'cancelled'}>
              <ConfiguratorStepper
                label={t('entity.newVehicle.order.deliveryStages', 'Delivery stages')}
                steps={LIFECYCLE_STATUSES.map((status) => ({
                  title: getNewVehicleEnumLabel('orderStatus', status, t),
                }))}
                currentIndex={currentIndex}
              />
            </Show>
          </DataStatus>
          <Show when={awaitingConfirmation}>
            {(awaiting) => (
              <Box data-testid="new-vehicle-awaiting-confirmation">
                <Alert
                  variant="warning"
                  layout="detailed"
                  title={awaiting.title}
                  actions={awaiting.actions}
                />
              </Box>
            )}
          </Show>
          {/* The backend compares the VINs; the arrival dialog keeps prefilling the vehicle VIN. */}
          <Show
            when={
              isNotNil(order) && isNotNil(order.vinConflict)
                ? {conflict: order.vinConflict, order}
                : null
            }
          >
            {(vin) => (
              <Box data-testid="new-vehicle-vin-conflict">
                <Alert
                  variant="warning"
                  title={t(
                    'entity.newVehicle.order.vinConflict',
                    'Factory VIN differs from the vehicle VIN'
                  )}
                  message={[
                    `${t('entity.newVehicle.order.factoryVin', 'Factory VIN')}: ${vin.conflict.value}`,
                    `${t('entity.newVehicle.order.vehicleVin', 'Vehicle VIN')}: ${
                      vin.order.vehicleVin ?? EMPTY_PLACEHOLDER
                    }`,
                  ]}
                />
              </Box>
            )}
          </Show>
          {/* VIN and business case come from the configuration, so they survive a failed order load. */}
          <AttributeColumns
            rows={buildArray<AttributesRow>(orderAttributes)
              .add({label: i18n.t('entity.vehicle.labels.vin'), value: detail.vehicle?.vin})
              .whenNotNil(businessCaseId, (id) => ({
                label: t('entity.newVehicle.labels.businessCase', 'Business case'),
                content: (
                  <Link
                    href={composePath(businessCaseRoutes.overview, {params: {id}})}
                    title={t('entity.newVehicle.actions.openBusinessCase', 'Open business case')}
                    size="small"
                    leftIcon="action/open_in_new"
                  />
                ),
              }))}
          />
          <Show when={detail.vehicle?.incoming}>
            <Box data-testid="new-vehicle-incoming">
              <Alert
                variant="info"
                type="inline"
                message={t(
                  'entity.newVehicle.order.incoming',
                  'Incoming — not in stock and not advertisable until arrival'
                )}
              />
            </Box>
          </Show>
        </VStack>
      </Card>
      <Show when={order?.arrival ? order : null}>
        {(order) => <ArrivalCard order={order} lines={detail.currentPriceSnapshot.lines} />}
      </Show>
      <Show when={order}>{(order) => <DeliveryUpdatesCard order={order} />}</Show>
    </VStack>
  );
}
